/** The one labeled form field (SA-1196, spec §5.3): the label, the required
 * star, the stale mark, the control (or a caller's own), the state tint and
 * the status line that is the control's accessible description. The
 * generated form, the intake surfaces and the Compass views all render it;
 * the Compass `FormField` it replaces took the same `required`, `describedBy`
 * and `control` props. */
import type { ReactNode } from 'react'
import { Sparkles } from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import { cn } from '@/utils'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import type { FieldPrefill, FieldSource, TaskFieldRead } from '@/types/process'
import { FieldInput } from './FieldInput'
import { fieldState, sourceTone, type FieldState, type SourceTone } from './fieldState'
import { CHANGED, REQUIRED, RESTORE, STALE_WORDS, sourceLine, wasText } from './fieldStateText'
import { RESTRICTED_VALUE } from './restrictedText'

/** What the block needs of a field: a `TaskFieldRead`, or an intake field shaped like one. */
export type FieldBlockField = Pick<TaskFieldRead, 'id' | 'label' | 'type' | 'options' | 'optional' | 'help'>
  & Partial<Pick<TaskFieldRead, 'value' | 'stale' | 'redacted' | 'columns' | 'min_rows' | 'alternatives' | 'source' | 'prefill'>>

export interface FieldBlockProps {
  field: FieldBlockField
  value: unknown
  onChange: (value: unknown) => void
  idPrefix: string
  /** Overrides the field's own `optional` ("What to change" is optional on the field, required by `rejected`). */
  required?: boolean
  /** An element id the control is also described by (SA-1050's price line). */
  describedBy?: string
  /** Replaces the typed control; it carries the id `${idPrefix}-${field.id}` so the label names it.
   * A function form receives that id and the description ids `FieldInput` would get (the status
   * line's id while it shows, then `describedBy`), so a custom control can wear both without
   * rebuilding them. A plain node renders as before. Either form is the caller's own control. */
  control?: ReactNode | ((ids: { id: string; describedBy: string | undefined }) => ReactNode)
  /** A module's own source and pre-fill, over the wire's (`referenceSource`, the intake's `extractedSource`). */
  source?: FieldSource | null
  prefill?: FieldPrefill | null
  /** Default: `onChange(prefill.value)`. A caller's own `control` gets the restore ONLY with
   * this: a stateful control (a select with its own choice) must move itself, or it desyncs. */
  onRestore?: (value: unknown) => void
}

const TONE_CLASS: Record<SourceTone, string> = {
  'role-ai': 'border-l-role-ai bg-role-ai/8',
  'role-automation': 'border-l-role-automation bg-role-automation/8',
  'role-person': 'border-l-role-person bg-role-person/8',
}

function tintFor(state: FieldState, source: FieldSource | null): string {
  if (state === 'required') return 'border-l-warning-strong bg-warning-strong/8'
  if (state === 'overridden') return TONE_CLASS['role-person']
  if (state === 'prefilled' && source) return TONE_CLASS[sourceTone(source.kind)]
  return 'border-l-transparent'
}

function SourceWords({ source, zone }: { source: FieldSource; zone: string }) {
  const words = sourceLine(source, new Date(), zone)
  if (source.kind !== 'proposal') return <>{words}</>
  return (
    <Badge variant="info" data-testid="proposal-chip"><Sparkles aria-hidden="true" />{words}</Badge>
  )
}

export function FieldBlock(props: FieldBlockProps) {
  const { field, value, onChange, idPrefix, required, describedBy, control, onRestore } = props
  const id = `${idPrefix}-${field.id}`
  const zone = useDefaultTimezone()
  const prefill = props.prefill !== undefined ? props.prefill : field.prefill ?? null
  const source = props.source !== undefined ? props.source : field.source ?? null
  const optional = required !== undefined ? !required : field.optional
  const state = fieldState({ optional, prefill }, value)
  // The restore sits BESIDE the status line, never inside it: the line is the
  // control's description, and a description should not read a button. Beside
  // a caller's own control only when the caller says how to restore it.
  const restorable = !control || onRestore !== undefined
  const restore = restorable && !field.redacted && prefill && (state === 'required' || state === 'overridden') ? (
    <Button type="button" size="sm" variant="ghost" className="h-auto px-1 py-0 text-xs"
      onClick={() => (onRestore ?? onChange)(prefill.value)}>
      {RESTORE}
    </Button>
  ) : null

  // A withheld field shows no status, no restore and no tint (spec ruling): a
  // "Required" beside "Restricted" would ask for a value the viewer may not see.
  let status: ReactNode = null
  if (field.redacted) status = null
  else if (state === 'required') status = REQUIRED
  else if (state === 'prefilled' && source) status = <SourceWords source={source} zone={zone} />
  else if (state === 'overridden' && prefill) {
    status = (
      <>
        {CHANGED} · <s>{wasText(prefill.value, field.type)}</s>
        {source && <> · {sourceLine(source, new Date(), zone)}</>}
      </>
    )
  }
  const statusId = `${id}-status`
  const described = [status ? statusId : null, describedBy].filter(Boolean).join(' ') || undefined

  return (
    <div data-state={state} className={cn('space-y-1 rounded-r-md border-l-2 pl-2', field.redacted ? 'border-l-transparent' : tintFor(state, source))}>
      <div className="flex items-center gap-1">
        {field.redacted
          ? <span className="text-sm font-medium">{field.label}</span>
          : <label htmlFor={id} className="text-sm font-medium">{field.label}</label>}
        {!optional && !field.redacted && <span aria-hidden="true" className="text-destructive">*</span>}
        {field.stale && <span className="text-xs text-warning-strong">{STALE_WORDS}</span>}
      </div>
      {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
      {field.redacted
        ? <p className="text-sm text-muted-foreground">{RESTRICTED_VALUE}</p>
        : typeof control === 'function'
          ? control({ id, describedBy: described })
          : control ?? <FieldInput field={field} value={value} onChange={onChange} idPrefix={idPrefix} describedBy={described} />}
      {(status || restore) && (
        <div className="flex flex-wrap items-center gap-x-1">
          {status && <p id={statusId} data-testid="field-status" className="text-xs text-muted-foreground">{status}</p>}
          {restore}
        </div>
      )}
    </div>
  )
}
